import React, { useState } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Autocomplete,
  Chip,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import 'dayjs/locale/en-gb';
import { useIDP } from '../contexts/IDPContext';
import EditableItemList from '../../../MyActivities/MyCV/components/common/EditableItemList';
import GoalStatusSelector from '../../../common/filters/GoalStatusSelector';
import { IDP_PRIORITY_OPTIONS, IDP_USAGE_STATUSES, IDP_USAGE_STATUS_OPTIONS, getUsageStatusLabel } from '../../../../constants/idpPriorities';
import { DynamicIcon } from '../../../Permissions/Roles/utils/iconMap';
import { toPickerValue } from '../../../../utils/dateOnly';

const GeneralTab = () => {
  const {
    goalData,
    updateGeneralInfo,
    updateResources,
    readonly,
    goalStatuses,
    goalStatusTransitions,
    resourceTemplates,
  } = useIDP();

  const [expandedResourceIndex, setExpandedResourceIndex] = useState(null);

  if (!goalData) return null;

  const currentStatusId = goalData.currentStatusId || goalData.currentStatus?.id;

  const handleFieldChange = (field) => (event) => {
    updateGeneralInfo({ [field]: event.target.value });
  };

  const handleDateChange = (field) => (newValue) => {
    if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
      updateGeneralInfo({ [field]: newValue.format('YYYY-MM-DD') });
    } else if (newValue === null) {
      updateGeneralInfo({ [field]: null });
    }
  };

  const handleStatusChange = (newStatusId, newStatus, reason) => {
    updateGeneralInfo({
      currentStatusId: newStatusId,
      currentStatus: newStatus,
      statusChangeReason: reason,
    });
  };

  const createNewResource = () => ({
    id: `temp-${Date.now()}`,
    name: '',
    details: '',
    usageStatus: IDP_USAGE_STATUSES.NOT_USED,
    developmentResourceTemplateId: null,
    resourceTemplate: null,
  });

  const renderResourceSummary = (resource) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
      <Box>
        <Typography variant="subtitle2" fontWeight={600}>
          {resource.name || 'Untitled Resource'}
        </Typography>
        {resource.resourceTemplate?.category && (
          <Typography variant="body2" color="text.secondary">
            Template: {resource.resourceTemplate.category}
          </Typography>
        )}
      </Box>
      {resource.usageStatus && (
        <Chip
          label={getUsageStatusLabel(resource.usageStatus)}
          size="small"
          variant="outlined"
          sx={{ ml: 2 }}
        />
      )}
    </Box>
  );

  const renderResourceEditForm = (resource, onChange) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Autocomplete
        options={resourceTemplates}
        getOptionLabel={(option) => option.category || ''}
        value={resource.resourceTemplate || null}
        onChange={(_, newValue) => {
          onChange({
            ...resource,
            resourceTemplate: newValue,
            developmentResourceTemplateId: newValue?.id || null,
          });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Resource Template"
            size="small"
            required
            helperText="Select a template or enter a custom resource"
          />
        )}
        disabled={readonly}
      />

      <TextField
        label="Resource Name"
        value={resource.name || ''}
        onChange={(e) => onChange({ ...resource, name: e.target.value })}
        size="small"
        fullWidth
        required
        disabled={readonly}
      />

      <TextField
        label="Details"
        value={resource.details || ''}
        onChange={(e) => onChange({ ...resource, details: e.target.value })}
        size="small"
        fullWidth
        multiline
        rows={2}
        disabled={readonly}
      />

      <FormControl size="small" fullWidth>
        <InputLabel>Usage Status</InputLabel>
        <Select
          value={resource.usageStatus || IDP_USAGE_STATUSES.NOT_USED}
          onChange={(e) => onChange({ ...resource, usageStatus: e.target.value })}
          label="Usage Status"
          disabled={readonly}
        >
          {IDP_USAGE_STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Basic Information Section */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Basic Information
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Title"
              value={goalData.title || ''}
              onChange={handleFieldChange('title')}
              fullWidth
              required
              disabled={readonly}
            />

            <TextField
              label="Description"
              value={goalData.description || ''}
              onChange={handleFieldChange('description')}
              fullWidth
              multiline
              rows={3}
              disabled={readonly}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={goalData.priority || ''}
                  onChange={handleFieldChange('priority')}
                  label="Priority"
                  disabled={readonly}
                >
                  {IDP_PRIORITY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <GoalStatusSelector
                currentStatusId={currentStatusId}
                statuses={goalStatuses}
                transitions={goalStatusTransitions}
                onStatusChange={handleStatusChange}
                disabled={readonly}
              />
            </Box>

            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
              <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker
                  label="Assigned Date"
                  value={toPickerValue(goalData.assignedAt)}
                  onChange={handleDateChange('assignedAt')}
                  disabled={readonly}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      inputProps: {
                        placeholder: 'DD/MM/YYYY'
                      }
                    }
                  }}
                />

                <DatePicker
                  label="Target Deadline"
                  value={toPickerValue(goalData.deadlineAt)}
                  onChange={handleDateChange('deadlineAt')}
                  disabled={readonly}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      inputProps: {
                        placeholder: 'DD/MM/YYYY'
                      }
                    }
                  }}
                />
              </Box>
            </LocalizationProvider>
          </Box>
        </Box>

        {/* Tracking Type Display */}
        {goalData.measurementType && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Tracking Method
            </Typography>
            <Box
              sx={{
                p: 2,
                backgroundColor: goalData.measurementType.color ? `${goalData.measurementType.color}15` : '#f8fafc',
                borderRadius: 1,
                border: `1px solid ${goalData.measurementType.color ? `${goalData.measurementType.color}50` : '#e2e8f0'}`,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              {goalData.measurementType.icon && (
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1,
                    backgroundColor: goalData.measurementType.color ? `${goalData.measurementType.color}25` : '#e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: goalData.measurementType.color || '#6b7280',
                  }}
                >
                  <DynamicIcon iconName={goalData.measurementType.icon} sx={{ fontSize: 28 }} />
                </Box>
              )}
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {goalData.measurementType.name}
                </Typography>
                {goalData.measurementType.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                    {goalData.measurementType.description}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Goal-Level Resources */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Goal Resources
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add resources that apply to the entire development goal.
          </Typography>
          <EditableItemList
            items={goalData.resources || []}
            setItems={updateResources}
            expandedIndex={expandedResourceIndex}
            setExpandedIndex={setExpandedResourceIndex}
            createNewItem={createNewResource}
            renderSummary={renderResourceSummary}
            renderEditForm={renderResourceEditForm}
            addButtonText="Add Resource"
            emptyState={{
              title: 'No resources added',
              description: 'Add resources to support this development goal.',
            }}
            itemName="resource"
            readonly={readonly}
          />
        </Box>
    </Box>
  );
};

export default GeneralTab;
